import type { FetchApi } from '../client';
import type { AuthenticationStrategy, MeResponse, UserApi } from './types';

// These API errors intentionally carry a stable, non-translated message and are
// translated at their display site (SignInForm / AcceptInvitationPage /
// PasswordSignInForm) with that component's `t`. Translation therefore no longer
// depends on the imported i18n singleton in this API client.
export class LoginError extends Error {
  constructor(
    message: string,
    public readonly requireExtraAuthenticationChallenge: boolean,
  ) {
    super(message);
  }
}

export class OAuthInteractionInvalidError extends Error {
  constructor() {
    super('Invalid OAuth interaction');
    this.name = 'OAuthInteractionInvalidError';
  }
}

export class AuthenticationStrategyInvalidEmailError extends Error {
  constructor() {
    super('Invalid email for authentication strategy');
    this.name = 'AuthenticationStrategyInvalidEmailError';
  }
}

export class UnexpectedAuthenticationStrategyError extends Error {
  constructor() {
    super('Unexpected authentication strategy error');
    this.name = 'UnexpectedAuthenticationStrategyError';
  }
}

export class InvitationAlreadyAcceptedError extends Error {
  constructor(public readonly body: Record<string, unknown> = {}) {
    super('Invitation already accepted');
    this.name = 'InvitationAlreadyAcceptedError';
  }
}

export const userApi = (fetchApi: FetchApi) => ({
  async login(
    email: string,
    password: string,
    authenticationChallenge: string,
  ): Promise<UserApi> {
    return fetchApi
      .post('/login', { email, password, authenticationChallenge })
      .then(async (response) => {
        if (response.status && response.status === 401) {
          const body = await response.json();
          throw new LoginError(
            'Login failed (401)',
            Boolean(body.requireExtraAuthentificationChallenge),
          );
        }
        return response;
      })
      .catch((error) => {
        if (error instanceof LoginError) {
          throw error;
        }
        // Message is translated at the display site (PasswordSignInForm), which
        // falls back to `user:user.login.error` when it is empty.
        throw new Error(error?.message ?? '');
      });
  },
  /**
   * Checks the credentials on behalf of the MCP OAuth server. The response holds
   * no app token: the webapp session is left untouched, and `redirectUrl` carries
   * a short-lived token that must never be logged.
   */
  async loginForOAuthInteraction({
    email,
    password,
    authenticationChallenge,
    oauthInteraction,
  }: {
    email: string;
    password: string;
    authenticationChallenge: string;
    oauthInteraction: string;
  }): Promise<{ redirectUrl: string }> {
    const response = await fetchApi
      .post('/login', {
        email,
        password,
        authenticationChallenge: authenticationChallenge || undefined,
        oauthInteraction,
      })
      .catch((error) => {
        if (error?.originalResponse?.status === 400) {
          throw new OAuthInteractionInvalidError();
        }
        throw new Error(error?.message ?? '');
      });
    if (response instanceof Response) {
      const body = await response.json();
      throw new LoginError(
        'Login failed (401)',
        Boolean(body.requireExtraAuthentificationChallenge),
      );
    }
    return response;
  },
  async me(token: string): Promise<MeResponse> {
    return fetchApi
      .setToken(token)
      .get('/me')
      .catch((error) => {
        throw new Error(error?.message ?? '');
      });
  },
  async signup(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    language: string,
    captchaToken: string, // Backend expects 'recaptcha' field, but accepts Turnstile tokens
    phoneNumber?: string,
  ): Promise<UserApi> {
    return fetchApi.post('/signup', {
      email,
      password,
      firstName,
      lastName,
      language,
      recaptcha: captchaToken, // Keep as 'recaptcha' for backend compatibility
      phoneNumber,
    });
  },
  async activate(email: string, token: string): Promise<UserApi> {
    return fetchApi.post('/activate', { email, token }).then((a) => {
      if (a.status && a.status === 302) {
        throw new Error('already activated');
      }
      return a;
    });
    // .catch(errorMessage => {
    //   throw new Error(errorMessage)
    // });
  },
  async sendResetToken(
    email: string,
    locale: string,
  ): Promise<{ success: boolean }> {
    return fetchApi
      .post('/send-reset-token', { email, locale })
      .catch((error) => {
        if (error instanceof Error) {
          throw new Error(
            `Unable to reset password using these credentials.. Please try again. (${error.message})`,
            { cause: error },
          );
        }
        throw new Error(
          'Unable to reset password using these credentials. Please try again.',
        );
      });
  },
  async validateResetToken(
    email: string,
    token: string,
  ): Promise<{ success: boolean }> {
    return fetchApi
      .post('/validate-reset-token', { email, token })
      .catch((error) => {
        if (error instanceof Error) {
          throw new Error(
            `Unable to reset password using these credentials. Please try again. (${error.message})`,
            { cause: error },
          );
        }
        throw new Error(
          'Unable to reset password using these credentials. Please try again.',
        );
      });
  },
  async resetPassword(
    email: string,
    password: string,
    token: string,
  ): Promise<UserApi> {
    return fetchApi
      .post('/reset-password', { email, token, password })
      .then(async (response) => {
        if (response.status && response.status === 401) {
          const error = await response.json();
          const errorMessage = error.message || JSON.stringify(error);
          throw new Error(errorMessage);
        }
        return response;
      })
      .catch((error) => {
        const errorMessage = error.message || JSON.stringify(error);
        throw new Error(`Unable to reset password. ${errorMessage}`, {
          cause: error,
        });
      });
  },
  async switchWorkspace(
    token: string,
    payload: {
      workspaceId: number;
    },
  ) {
    return fetchApi.setToken(token).post('/switch-workspace', payload);
  },
  async getJobTitles({
    token,
  }: {
    token: string;
  }): Promise<{ id: number; title: string; description: string }[]> {
    return fetchApi
      .setToken(token)
      .get('/occupations')
      .catch((error) => {
        if (error instanceof Error) {
          throw new Error(
            `Unable to fetch job titles. Please try again. (${error.message})`,
            { cause: error },
          );
        }
        throw new Error(
          'Unable to fetch job titles. Please try again. (errorMessage))',
        );
      });
  },
  async addUserJobTitle({
    token,
    occupationId,
    customOccupationTitle,
  }: {
    token: string;
    occupationId: number | undefined;
    customOccupationTitle: string | undefined;
  }): Promise<UserApi> {
    const params: Record<string, number | string> = {};
    if (occupationId !== undefined) {
      params.occupationId = occupationId;
    } else if (customOccupationTitle !== undefined) {
      params.customOccupationTitle = customOccupationTitle;
    }

    return fetchApi
      .setToken(token)
      .post('/set-user-occupation', params)
      .catch((error) => {
        if (error instanceof Error) {
          throw new Error(
            `Unable to update Job Title. Please try again. (${error.message})`,
            { cause: error },
          );
        }
        throw new Error(
          'Unable to update Job Title. Please try again. (errorMessage))',
        );
      });
  },
  async createOrganization({
    token,
    name,
    sector,
    isCustomSector,
  }: {
    token: string;
    name: string;
    sector: string | number;
    isCustomSector: boolean;
  }): Promise<Response> {
    return fetchApi.setToken(token).post(
      '/create-organization',
      {
        name,
        sector,
        isCustomSector,
      },
      true,
    );
  },
  async getOrganizationSectors({
    token,
  }: {
    token: string;
  }): Promise<{ id: number; name: string; description: string }[]> {
    return fetchApi
      .setToken(token)
      .get('/organization-sectors')
      .catch((error) => {
        if (error instanceof Error) {
          throw new Error(
            `Unable to fetch organization sectors. Please try again. (${error.message})`,
            { cause: error },
          );
        }
        throw new Error(
          `Unable to fetch organization sectors. Please try again. (${error})`,
        );
      });
  },
  async joinOrganizationWithInvitation({
    token,
    invitationKey,
  }: {
    token: string;
    invitationKey: string;
  }): Promise<UserApi> {
    return fetchApi
      .setToken(token)
      .post('/join-organization', {
        invitationKey,
      })
      .catch((error) => {
        if (error instanceof Error) {
          throw new Error(
            `Unable to accept invitation. Please try again. (${error.message})`,
            { cause: error },
          );
        }
        throw new Error(
          `Unable to accept invitation. Please try again. (${error})`,
        );
      });
  },
  async acceptInvitation({
    invitationKey,
    firstName,
    lastName,
    password,
    language,
  }: {
    invitationKey: string;
    firstName: string;
    lastName: string;
    password: string;
    language: string;
  }): Promise<UserApi> {
    return fetchApi
      .post('/accept-invitation', {
        invitationKey,
        firstName,
        lastName,
        password,
        language,
      })
      .catch((error) => {
        if (error instanceof Error) {
          throw new Error(
            `Unable to accept invitation. Please try again. (${error.message})`,
            { cause: error },
          );
        }
        throw new Error(
          `Unable to accept invitation. Please try again. (${error})`,
        );
      });
  },

  async getInvitation({
    invitationKey,
  }: {
    invitationKey: string;
  }): Promise<UserApi> {
    return fetchApi.get(`/invitation/${invitationKey}`).catch((error) => {
      if (error.originalResponse?.status === 409) {
        throw new InvitationAlreadyAcceptedError();
      }
      if (error instanceof Error) {
        throw new Error(
          `Unable to get invitation. Please try again. (${error.message})`,
          { cause: error },
        );
      }
      throw new Error(`Unable to get invitation. Please try again. (${error})`);
    });
  },

  async getAuthenticationStrategy(
    email: string,
  ): Promise<AuthenticationStrategy> {
    return fetchApi
      .get(`/authentication-strategy?email=${encodeURIComponent(email)}`, {
        cache: false,
      })
      .catch((error) => {
        if (error.originalResponse?.status === 422) {
          throw new AuthenticationStrategyInvalidEmailError();
        }
        throw new UnexpectedAuthenticationStrategyError();
      });
  },
});
